import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Package, Globe2, Sprout, Factory, Container, Ship, MessageCircle, CheckCircle2, Award } from "lucide-react";
import { BRAND, wa } from "@/lib/brand";
import { CATEGORIES } from "@/data/products";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import QuoteModal from "@/components/QuoteModal";
import Reveal from "@/components/Reveal";
import { useTitle } from "@/lib/useTitle";

const HERO_BG = "https://images.unsplash.com/photo-1470687372616-d48d122e3b06?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const MILL_BG = "https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const journey = [
  { n: "01", title: "Source", icon: Sprout, desc: "Trusted Indian agricultural sourcing partners across origin belts." },
  { n: "02", title: "Process", icon: Factory, desc: "Professional mills and processing facilities." },
  { n: "03", title: "Quality", icon: ShieldCheck, desc: "Quality checks and buyer-approved specifications." },
  { n: "04", title: "Pack", icon: Package, desc: "Export-ready packaging with customization options." },
  { n: "05", title: "Load", icon: Container, desc: "Container stuffing and logistics coordination." },
  { n: "06", title: "Deliver", icon: Ship, desc: "Supply to agreed destination port or warehouse." },
];

const whyUs = [
  { title: "Consistent Quality", desc: "Aligned to buyer specifications, verified at every stage." },
  { title: "Reliable Sourcing", desc: "Trusted origin partners across India's key agri belts." },
  { title: "Bulk Supply Capability", desc: "FCL 20ft / 40ft containers, scalable order volumes." },
  { title: "Export-Ready Packaging", desc: "25 / 50 kg PP, jute and private-label options." },
  { title: "Competitive Pricing", desc: "Direct sourcing, transparent trade commercials." },
  { title: "Flexible Specifications", desc: "Custom moisture, broken %, packaging as required." },
  { title: "Buyer-Focused Service", desc: "Single point of contact for the full trade flow." },
  { title: "Transparent Communication", desc: "Documentation, samples and pre-shipment updates." },
];

const Home = () => {
  useTitle("GN Agro Traders | Premium Indian Rice & Agro Commodity Exporter",
    "GN Agro Traders supplies premium Basmati Rice, Non-Basmati Rice, Pulses, Wheat, Flour and agricultural commodities from India for global B2B buyers.");
  const [modal, setModal] = useState(false);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    fetchProducts().then((list) =>
      setFeatured(list.filter((p) => ["1121-basmati-steam", "1509-basmati", "traditional-basmati", "sugandha-rice", "sharbati-rice", "pusa-basmati"].includes(p.id)))
    );
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#002B18]/70 via-[#002B18]/45 to-[#002B18]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28 sm:pt-32 sm:pb-36 lg:pt-40 lg:pb-44 text-[#F4F4ED]">
          <div className="fade-up inline-flex items-center gap-2 text-[11px] font-eyebrow text-[#D4AF37]">
            <span className="gold-line" /> Indian Agricultural Export Partner
          </div>
          <h1 className="fade-up-delay-1 mt-4 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] text-white">
            Pure Grains. <span className="italic text-[#D4AF37]">Global Standards.</span>
          </h1>
          <p className="fade-up-delay-2 mt-5 max-w-2xl text-[15px] sm:text-lg text-white/85">
            Premium Indian Rice, Pulses, Wheat & Agro Commodities for global markets. Reliable sourcing. Consistent quality. Export-ready supply.
          </p>
          <div className="fade-up-delay-3 mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 rounded-full btn-gold px-6 py-3 text-xs font-semibold uppercase tracking-widest">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/products" data-testid="hero-products-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-white/10">
              Explore Products
            </Link>
            <a href={wa()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest">
              <MessageCircle className="h-4 w-4" /> Talk to Us
            </a>
          </div>
          <div className="fade-up-delay-3 mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 text-white/90">
            {["Traceable Indian Sourcing", "APEDA-Aligned Export Practices", "Custom 25 / 50 kg Packaging", "FCL 20ft & 40ft Dispatch"].map((t, i) => (
              <div key={i} className="text-xs sm:text-sm border-l-2 border-[#D4AF37]/60 pl-3">{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />The Company</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#004225] leading-tight">Connecting Indian Agricultural Excellence to Global Markets</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <p className="text-[15px] leading-relaxed text-[#171717]/80">
                GN Agro Traders is an India-based agro trading and export supply company specializing in premium Basmati and Non-Basmati Rice, Pulses, Wheat, Flour and other agricultural commodities. We work with trusted sourcing partners, mills and processing units across India to deliver consistent quality, competitive pricing and reliable bulk supply for international buyers.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/about" className="inline-flex items-center gap-1.5 text-[#004225] text-sm font-semibold border-b border-[#004225]/40 hover:border-[#D4AF37]">About GN Agro Traders <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/global-supply" className="inline-flex items-center gap-1.5 text-[#004225] text-sm font-semibold border-b border-[#004225]/40 hover:border-[#D4AF37]">Global Supply <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-white border-y border-[#004225]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Reveal><div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />Product Portfolio</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#004225]">A Curated Export Catalogue</h2></Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.id} delay={i * 60}>
                <Link to={`/products/${c.id}`} data-testid={`category-${c.id}`} className="block bg-[#F4F4ED] hover:bg-[#F7F2E2] border border-[#004225]/10 rounded-sm p-5 card-elevate">
                  <div className="text-[10px] font-eyebrow text-[#B5942D]">{c.tag}</div>
                  <div className="mt-2 font-display text-lg text-[#004225]">{c.name}</div>
                  <div className="mt-4 text-[11px] uppercase tracking-widest text-[#004225]/70 flex items-center gap-1">View <ArrowRight className="h-3 w-3" /></div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Reveal><div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />Why Buyers Choose Us</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#004225]">Why Global Buyers Choose GN Agro Traders</h2></Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {whyUs.map((w, i) => (
            <Reveal key={w.title} delay={i * 40}>
              <div className="bg-white border border-[#004225]/10 rounded-sm p-6 h-full card-elevate">
                <CheckCircle2 className="h-5 w-5 text-[#D4AF37]" />
                <div className="mt-3 font-display text-lg text-[#004225]">{w.title}</div>
                <p className="mt-2 text-[13px] text-[#171717]/75 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="relative py-24 overflow-hidden" data-testid="journey-section">
        <div className="absolute inset-0"><img src={MILL_BG} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-[#F4F4ED]/85" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />Farm → Global Buyer</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#004225]">From Indian Farms to Global Ports</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {journey.map((s, i) => (
              <Reveal key={s.n} delay={i * 90}>
                <div className="bg-white border border-[#004225]/10 rounded-sm p-5 h-full">
                  <div className="text-[10px] font-eyebrow text-[#D4AF37]">{s.n}</div>
                  <s.icon className="h-6 w-6 text-[#004225] mt-2" />
                  <div className="mt-3 font-display text-lg text-[#004225]">{s.title}</div>
                  <p className="mt-2 text-[12px] text-[#171717]/75 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BASMATI */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-eyebrow text-[#B5942D]"><span className="gold-line mr-3" />Featured Basmati</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#004225]">Signature Basmati Varieties</h2>
          </div>
          <Link to="/products/basmati-rice" className="text-[#004225] text-sm font-semibold border-b border-[#004225]/40 hover:border-[#D4AF37] inline-flex items-center gap-1">View all Basmati <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((p) => (<ProductCard key={p.id} product={p} />))}
        </div>
      </section>

      {/* GLOBAL SUPPLY */}
      <section className="bg-[#002B18] text-white grain relative overflow-hidden" data-testid="global-supply-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />Global Trade</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white">Supplying According to Buyer Requirements and Destination Specifications</h2>
            <p className="mt-4 text-white/80 text-[15px] leading-relaxed">From Indian ports of Mundra and Nhava Sheva to destination ports across the Middle East, Europe, Africa and Southeast Asia — we coordinate FOB, CFR and CIF shipments in line with buyer requirements.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/global-supply" className="rounded-full btn-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">See Global Supply</Link>
              <Link to="/logistics" className="rounded-full border border-white/30 text-white px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-white/10">Logistics Overview</Link>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-white/10">
              <svg viewBox="0 0 800 500" className="w-full h-full">
                <defs>
                  <linearGradient id="oceang" x1="0" x2="1"><stop offset="0" stopColor="#0b3b28" /><stop offset="1" stopColor="#0e5236" /></linearGradient>
                </defs>
                <rect width="800" height="500" fill="url(#oceang)" />
                <g fill="#F4F4ED" opacity="0.9">
                  <path d="M120,180 q40,-40 90,-20 t120,10 t60,40 t-60,60 t-120,20 t-80,-50z" />
                  <path d="M340,220 q60,-50 140,-40 t150,30 t60,50 t-60,80 t-140,40 t-160,-40 t10,-120z" />
                  <path d="M170,320 q30,-10 50,10 t40,60 t-50,50 t-60,-40z" />
                </g>
                <g stroke="#D4AF37" strokeWidth="1.4" fill="none" className="route-anim">
                  <path d="M470,300 C520,180 620,150 690,120" />
                  <path d="M470,300 C560,230 640,240 720,220" />
                  <path d="M470,300 C400,260 300,240 200,210" />
                  <path d="M470,300 C500,360 520,400 560,430" />
                </g>
                <g fill="#D4AF37">
                  <circle cx="470" cy="300" r="5" />
                  <circle cx="690" cy="120" r="4" />
                  <circle cx="720" cy="220" r="4" />
                  <circle cx="200" cy="210" r="4" />
                  <circle cx="560" cy="430" r="4" />
                </g>
                <g fill="#F4F4ED" fontSize="10" fontFamily="Outfit">
                  <text x="480" y="295">India</text>
                  <text x="640" y="115">Europe</text>
                  <text x="675" y="215">GCC</text>
                  <text x="150" y="205">Americas</text>
                  <text x="520" y="445">Africa / SEA</text>
                </g>
              </svg>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CERTIFICATIONS + QUALITY teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-8">
        <Reveal>
          <div className="bg-white border border-[#004225]/10 rounded-sm p-8 h-full card-elevate">
            <Award className="h-6 w-6 text-[#D4AF37]" />
            <div className="mt-3 text-[11px] font-eyebrow text-[#B5942D]">Certifications & Compliance</div>
            <h3 className="mt-2 font-display text-2xl text-[#004225]">Verified Export Documentation</h3>
            <p className="mt-3 text-[13px] text-[#171717]/75 leading-relaxed">APEDA, IEC, GST, LLP, FSSAI and MSME frameworks aligned to Indian export practices. Third-party inspections available on request through SGS / Intertek / Bureau Veritas.</p>
            <Link to="/certifications" className="mt-5 inline-flex items-center gap-1 text-[#004225] text-sm font-semibold border-b border-[#004225]/40 hover:border-[#D4AF37]">View Certifications <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="bg-white border border-[#004225]/10 rounded-sm p-8 h-full card-elevate">
            <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
            <div className="mt-3 text-[11px] font-eyebrow text-[#B5942D]">Quality Assurance</div>
            <h3 className="mt-2 font-display text-2xl text-[#004225]">Quality at Every Stage</h3>
            <p className="mt-3 text-[13px] text-[#171717]/75 leading-relaxed">Supplier selection, product specification, sample approval, quality inspection, packaging verification, pre-shipment coordination and documentation.</p>
            <Link to="/quality" className="mt-5 inline-flex items-center gap-1 text-[#004225] text-sm font-semibold border-b border-[#004225]/40 hover:border-[#D4AF37]">Quality Process <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>
      </section>

      {/* TESTIMONIALS placeholder */}
      <section className="bg-[#F7F2E2]/60 border-y border-[#004225]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="text-[11px] font-eyebrow text-[#B5942D]">Buyer Feedback</div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl text-[#004225]">Verified Buyer Testimonials Coming Soon</h3>
          <p className="mt-3 text-[13px] text-[#171717]/70 max-w-2xl mx-auto">Once buyer engagements progress to verified references, real testimonials will appear here — GN Agro Traders does not publish fabricated feedback.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="relative overflow-hidden rounded-sm border border-[#004225]/10 bg-[#004225] text-white p-10 sm:p-16 grain">
          <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />Let&apos;s Talk Business</div>
          <h3 className="mt-3 font-display text-3xl sm:text-5xl leading-tight">Let&apos;s Build a Reliable Supply Partnership.</h3>
          <p className="mt-4 max-w-2xl text-white/80 text-[15px]">Share your product specifications, quantity and destination. Our team will work with you on the appropriate sourcing, quality and commercial requirements.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" data-testid="final-quote-btn" className="rounded-full btn-gold px-6 py-3 text-xs font-semibold uppercase tracking-widest">Request a Quote</Link>
            <Link to="/contact" className="rounded-full border border-white/30 text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-white/10">Contact GN Agro Traders</Link>
            <button onClick={() => setModal(true)} data-testid="open-quote-modal" className="rounded-full bg-white/10 text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-white/20">Quick Enquiry</button>
          </div>
        </div>
      </section>

      <QuoteModal open={modal} onOpenChange={setModal} source="home-cta" />
    </div>
  );
};

export default Home;
